import { path, pipe, partial } from 'ramda'

import { i18n, type NumberFormats, type KeyOfDatetimeFormats } from '@/i18n'
import { CURRENT_LOCALE } from '@/const'

const { t: $t, d: $d, n: $n } = i18n.global

/**
 * Represents a table field with a key, label, and resolver function.
 * The resolver function is used to transform the value of the field for display in the table.
 */
export class TableField {
  /**
   * Constructs a new instance of TableField.
   *
   * @param key - The unique identifier for the table field.
   * @param label - The human-readable label for the table field.
   * @param resolver - A function that takes any number of arguments and returns the transformed value for display in the table.
   */
  constructor(
    public key: string,
    public label: string,
    public resolver: (...arg: any[]) => any
  ) {}

  /**
   * Calls the resolver function with the provided arguments and returns the transformed value.
   *
   * @param args - Any number of arguments to be passed to the resolver function.
   * @returns The transformed value returned by the resolver function.
   */
  getValue(...args: any[]): any {
    return this.resolver.apply(undefined, args)
  }
}

/**
 * Converts a string or array of strings to an array of strings.
 *
 * @param key - A string or an array of strings.
 * @returns An array of strings.
 */
const ar = (key: string | string[]) => (Array.isArray(key) ? key : [key])

/**
 * A higher-order function that normalizes the `key` parameter passed to a function.
 * It converts the `key` parameter to an array of strings, if it is not already an array.
 * This allows the function to handle both string and array of string inputs for the `key` parameter.
 *
 * @param func - The function to be wrapped, which takes a `key` parameter and additional arguments.
 * @returns A new function that normalizes the `key` parameter before calling the original function.
 */
const keyNormalizer =
  (func: (...arg: any[]) => any) =>
  (key: string | string[], ...attrs: any[]) =>
    func(ar(key), ...attrs)

/**
 * Transforms a datetime value into a formatted string for display in a table.
 *
 * @param config - The configuration options for formatting the datetime value.
 *                  It should be a key from the `KeyOfDatetimeFormats` type, which specifies the formatting options.
 * @param value - The datetime value to be transformed. It can be a string or a number.
 *
 * @returns The formatted datetime string by current locale. If the `value` is falsy, it returns a dash ('-').
 *
 * @example
 * ```typescript
 * import { KeyOfDatetimeFormats } from '@/i18n';
 *
 * const formattedDatetime = transformDatetime('datetime', '2022-01-01T12:00:00Z');
 * console.log(formattedDatetime); // Output: "Jan 1, 2022, 12:00 PM"
 *
 * const formattedEmptyDatetime = transformDatetime('datetime', '');
 * console.log(formattedEmptyDatetime); // Output: "-"
 * ```
 */
function transformDatetime(config: KeyOfDatetimeFormats, value: any) {
  if (value) {
    return $d(new Date(value), config, CURRENT_LOCALE)
  }
  return $t('-')
}

/**
 * Transforms a number value into a formatted string for display in a table.
 *
 * @param config - The configuration options for formatting the number value.
 *                  It should be a key from the `NumberFormats` type, which specifies the formatting options.
 * @param value - The number value to be transformed. It can be a string or a number.
 *
 * @returns The formatted number string by current locale for the `EUR` currency.
 *
 * @example
 * ```typescript
 * import { NumberFormats } from '@/i18n';
 *
 * const formattedNumber = transformNumber('currency', 12345.67);
 * console.log(formattedNumber); // Output: "€12,345.67"
 * ```
 */
function transformNumber(config: keyof NumberFormats, value: string | number) {
  return $n(Number(value), config)
}

/**
 * An instance of `TableField` representing an empty table field.
 * Used as a placeholder for empty a table field.
 * The `key` is an empty string, the `label` is an empty string, and the `resolver` function always returns an empty string.
 */
export const emptyTableField = new TableField('', '', () => '')

/**
 * Creates a new `TableField` instance with the provided `key`, `label`, and `path` resolver.
 * The `key` parameter is normalized to an array of strings using the `keyNormalizer` function.
 *
 * @param key - The key for the table field, which can be a string or an array of strings.
 * @param label - The label for the table field.
 * @returns A new `TableField` instance with the provided `key`, `label`, and `path` resolver.
 */
export const tableField = keyNormalizer(
  (key, label) => new TableField(key.join('_'), label, path(key))
)

/**
 * Creates a resolver function that transforms a datetime value into a formatted string for display in a table.
 *
 * @param key - The key or path to the datetime value.
 * @param config - The configuration options for formatting the datetime value. Defaults to 'datetime'.
 * @returns A function that takes any number of arguments and returns the formatted datetime string, or a dash ('-') if the value is falsy.
 */
export const tableFieldDateResolver = (
  key: string,
  config = 'datetime'
): ((...arg: any[]) => any) => pipe(key, partial(transformDatetime, [config]))

/**
 * Creates a new `TableField` instance with the provided `key`, `label`, and a datetime resolver function.
 * The `key` parameter is normalized to an array of strings using the `keyNormalizer` function.
 * The datetime resolver function is created using the `tableFieldDateResolver` function, which transforms a datetime value into a formatted string for display in a table.
 *
 * @param key - The key for the table field, which can be a string or an array of strings.
 * @param label - The label for the table field.
 * @param config - The configuration options for formatting the datetime value. Defaults to 'datetime'.
 * @returns A new `TableField` instance with the provided `key`, `label`, and datetime resolver function.
 */
export const tableFieldDate = keyNormalizer(
  (key, label, config) =>
    new TableField(
      key.join('_'),
      label,
      tableFieldDateResolver(path(key), config)
    )
)

/**
 * Creates a new `TableField` instance with the provided `key`, `label`, and a datetime resolver function.
 * The `key` parameter is normalized to an array of strings using the `keyNormalizer` function.
 * The datetime resolver function is created using the `tableFieldDateResolver` function, which transforms a datetime value into a formatted string for display in a table.
 *
 * @param key - The key for the table field, which can be a string or an array of strings.
 * @param label - The label for the table field.
 * @param pathTo - The path to the value.
 * @param config - The configuration options for formatting the value. Defaults to 'datetime'.
 * @returns A new `TableField` instance with the provided `key`, `label`, and datetime resolver function.
 */
export const tableFieldDateWithResolver = keyNormalizer(
  (key, label, pathTo, config) =>
    new TableField(
      key.join('_'),
      label,
      tableFieldDateResolver(pipe(path(pathTo)), config)
    )
)

/**
 * Creates a resolver function that transforms a number value into a formatted string for display in a table.
 *
 * @param key - The key or path to the number value.
 * @param config - The configuration options for formatting the number value. Can be null.
 * @returns A function that takes any number of arguments and returns the formatted number string.
 */
export const tableFieldNumberResolver = (
  key: string,
  config = null
): ((...arg: any[]) => any) => pipe(key, partial(transformNumber, [config]))

/**
 * Creates a new `TableField` instance with the provided `key`, `label`, and a number resolver function.
 * The `key` parameter is normalized to an array of strings using the `keyNormalizer` function.
 * The number resolver function is created using the `tableFieldNumberResolver` function, which transforms a number value into a formatted string for display in a table.
 *
 * @param key - The key for the table field, which can be a string or an array of strings.
 * @param label - The label for the table field.
 * @param config - The configuration options for formatting the number value. Can be null.
 * @returns A new `TableField` instance with the provided `key`, `label`, and number resolver function.
 */
export const tableFieldNumber = keyNormalizer(
  (key, label, config = null) =>
    new TableField(
      key.join('_'),
      label,
      pipe(path(key), partial(transformNumber, [config]))
    )
)

/**
 * Creates a new `TableField` instance with the provided `key`, `label`, and a number resolver function.
 * The `key` parameter is normalized to an array of strings using the `keyNormalizer` function.
 * The number resolver function is created using the `tableFieldNumberResolver` function, which transforms a number value into a formatted string for display in a table.
 *
 * @param key - The key for the table field, which can be a string or an array of strings.
 * @param label - The label for the table field.
 * @param pathTo - The path to the value.
 * @param config - The configuration options for formatting the number value. Can be null. Defaults to null.
 * @returns A new `TableField` instance with the provided `key`, `label`, and number resolver function.
 */
export const tableFieldNumberWithResolver = keyNormalizer(
  (key, label, pathTo, config) =>
    new TableField(
      key.join('_'),
      label,
      tableFieldNumberResolver(pipe(path(pathTo)), config)
    )
)
